import React, { useEffect, useMemo, useRef, useState } from "react";
import LeftMenuSidebar from "../../../unicomponents/LeftMenuSidebar";
import useAllForms from "../../../hooks/useAllForms";
import { useLocation } from "react-router-dom";
import useComponentsToForms from "../../../hooks/useComponentsToForms";
import DynamicComponentsRenderer from "../../../unicomponents/formComponents/DynamicComponentsRenderer";
import useOrganizationSetting from "../../../hooks/useOrganizationSetting";
import useRegistrationInfo from "../../../hooks/useRegistrationInfo";
import { useUIContext } from "../../../context/UIContext";
import SchoolLifeRecordFormButtonBar from "./components/SchoolLifeRecordFormButtonBar";
import { useReactToPrint } from "react-to-print";
import DropdownSchoolYear from "../../../unicomponents/Dropdowns/DropdownSchoolYear";
import { generateAllRegisteredStudents } from "../../../util/dealingArrObj";
import { useAuthContext } from "../../../context/AuthContext";
import { checkHrteachers } from "../../../util/checkPermissions";
import SimpleAlertMessageBlock from "../../../unicomponents/SimpleAlertMessageBlock";
import LoadingInside from "../../../unicomponents/LoadingInside";
import { useTranslation } from "react-i18next";
import styles from "./css/SchoolLifeRecord.module.css";
import { dateTokoreanStyleYearMonthDay } from "../../../util/formatdate";
import useFlexibleData from "../../../hooks/useFlexibleData";

// Default optional 컴포넌트 목록 (NICE 스타일)
const DEFAULT_OPTIONAL_LABELS = {
  RegiPictureTable: "인적·학적사항",
  AllAttendance: "출결상황",
  AllAwardTableForm: "수상경력",
  AllCertificateTableForm: "자격증 및 인증 취득상황",
  AllCreativeActivityForm: "창의적 체험활동상황",
  AllVolunteerWorkRecordForm: "봉사활동",
  AllGeneralGradeForm: "교과학습발달상황",
  AllReadingRecordForm: "독서 활동 상황",
  AllHrTeacherComment: "행동특성 및 종합의견",
};

// optional 라벨 반환 헬퍼 (undefined면 optional 아님)
const getOptionalLabel = (item) =>
  item?.customized_contents?.optional ??
  item?.components?.contents?.optional ??
  DEFAULT_OPTIONAL_LABELS[item?.components?.component_filename];

const DEFAULT_PRINT_FOOTER_SLOTS = {
  left: [{ type: "schoolName" }],
  center: [{ type: "issueDate" }, { type: "pageNo" }],
  right: [{ type: "studentName" }],
};

const escapeForCssContent = (value = "") =>
  String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"').replace(/\n/g, " ");

const normalizeFooterToken = (token) => {
  if (!token) return null;
  if (typeof token === "string") return { type: token };
  if (typeof token === "object" && token.type) return token;
  return null;
};

const normalizeFooterSlot = (slotTokens, fallbackTokens) => {
  if (!Array.isArray(slotTokens) || slotTokens.length < 1) {
    return fallbackTokens;
  }

  const normalized = slotTokens.map(normalizeFooterToken).filter(Boolean);
  return normalized.length > 0 ? normalized : fallbackTokens;
};

const resolveSchoolDisplayName = (settingData, schoolId) => {
  if (!Array.isArray(settingData) || settingData.length < 1) {
    return schoolId || "";
  }

  const schoolNameKeys = [
    "school_name",
    "schoolName",
    "organization_name",
    "organizationName",
    "name",
  ];

  for (const key of schoolNameKeys) {
    const found = settingData.find((row) => row?.[key]);
    if (found?.[key]) return found[key];
  }

  const topLevelGroup = settingData.find(
    (row) =>
      row?.hierarchy_level === 0 &&
      row?.parent_id === 0 &&
      typeof row?.group_name === "string",
  );
  if (topLevelGroup?.group_name) return topLevelGroup.group_name;

  return schoolId || "";
};

const buildFooterSlotContent = (tokens, context) => {
  if (!Array.isArray(tokens) || tokens.length < 1) return "";

  const cssParts = [];
  const pushText = (text) => {
    if (!text) return;
    cssParts.push(`"${escapeForCssContent(text)}"`);
  };

  tokens.forEach((rawToken) => {
    const token = normalizeFooterToken(rawToken);
    if (!token) return;

    const tokenType = token.type;
    if (tokenType === "schoolName") {
      pushText(context.schoolName);
      return;
    }

    if (tokenType === "issueDate") {
      pushText(context.issueDate);
      return;
    }

    if (tokenType === "studentName") {
      const includeLabel = token.includeLabel !== false;
      const label = includeLabel ? token.label || "성명: " : "";
      const useOtherName =
        typeof token.useOtherName === "boolean"
          ? token.useOtherName
          : context.useOtherName;
      const studentName = useOtherName
        ? context.otherName || context.fullName
        : context.fullName;
      pushText(`${label}${studentName || ""}`);
      return;
    }

    if (tokenType === "customText") {
      pushText(token.text || "");
      return;
    }

    if (tokenType === "pageNo") {
      if (token.showTotal === false) {
        cssParts.push("counter(page)");
      } else {
        cssParts.push('counter(page) " / " counter(pages)');
      }
    }
  });

  return cssParts.join(' " " ');
};

/*
고등 고정이고, "고등"이라는 이름을 기준으로 재적에서 정보 가져오고, 
그를 바탕으로 다른 정보들 다 학년도 기준으로 필터해서 가져온다.
이름 검색하여 학번 선택하면 나오는 방식

keyArray 만든 것에서, schoolYearDates를 처리해서 학년도별 시작과 끝을 정리하자. 
아... 시상 때문에 학기 period 정보도 있어야 하네.. ㅠ.ㅠ
*/

export default function SchoolLifeRecordForm() {
  const { t } = useTranslation();
  const location = useLocation();
  const paths = location.pathname.split("/");
  const schoolId = paths[1];
  const [filters, setFilters] = useState({
    schoolYear: "",
    schoolYearId: "",
    selectedStudent: "",
  });
  const [registrationArray, setRegistrationArray] = useState([]);
  const [keyArray, setKeyArray] = useState([]);
  const [modifyMode, setModifyMode] = useState(false);
  const [searchKey, setSearchKey] = useState("");
  const [studentList, setStudentList] = useState("");
  const [usernameList, setUsernameList] = useState([]);
  const [showPageNumbers, setShowPageNumbers] = useState(false);
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);

  // optional 항목 표시 상태 (NICE 스타일)
  const [visibleOptionalIds, setVisibleOptionalIds] = useState(new Set());

  // 교과학습 항목 표시 상태
  // const [gradeShowAll, setGradeShowAll] = useState(true);
  const [gradeColumnVisibility, setGradeColumnVisibility] = useState({
    rank: true,
    rankClass: true,
    convert100Ratio: true,
    convert100: true,
    studentCount: true,
    standardDeviation: true,
    subjectAverage: true,
  });

  // 마지막 학년도 일부정보 미제공 상태
  const [lastYearShowAll, setLastYearShowAll] = useState(true);
  const [lastYearShow2ndSemester, setLastYearShow2ndSemester] = useState(true);

  const [activeMenu, setActiveMenu] = useState({
    menu_name: "",
    menu_id: "",
    description: "",
  });

  const conditions = {
    schoolid: schoolId,
    form_category: "schoolLifeRecord",
  };

  const [formQueryConditions, setFormQueryConditions] = useState({
    schoolid: schoolId,
    form_id: "",
  });

  const {
    allFormsQuery: { data: allFormsData },
  } = useAllForms(conditions);

  const {
    componentsToFormsQuery: { data: componentsToFormsData },
  } = useComponentsToForms(formQueryConditions);

  const {
    registrationInfoQuery: { isLoading, data: registrationInfoData },
    hrTeachersQuery: { data: hrTeachersData },
  } = useRegistrationInfo({ schoolid: schoolId }, { hrTeachers: true });

  const {
    settingQuery: { data: settingData },
  } = useOrganizationSetting({ schoolid: schoolId });

  const {
    flexibleDataQuery: { data: flexibleData },
  } = useFlexibleData({
    schoolid: conditions?.schoolid,
    main_category: "schoolLifeRecord",
  });

  const { setIsLoadingModal, handleToastCenterTop, handleToastCenterBottom } =
    useUIContext();
  const { hasPermission } = useAuthContext();

  useEffect(() => {
    if (allFormsData) {
      setFormQueryConditions((prev) => ({
        ...prev,
        form_id: allFormsData[0].id,
      }));
      setActiveMenu({
        menu_name: allFormsData[0].form_name,
        menu_id: allFormsData[0].id,
        description: allFormsData[0].description,
      });
    }
  }, [allFormsData]);

  // componentsToFormsData 변경 시 optional 항목 전체 선택으로 초기화
  useEffect(() => {
    if (componentsToFormsData && componentsToFormsData.length > 0) {
      const optionalIds = new Set(
        componentsToFormsData
          .filter((item) => getOptionalLabel(item))
          .map((item) => item.id),
      );
      setVisibleOptionalIds(optionalIds);
    }
  }, [componentsToFormsData]);

  // optional 항목 목록 (order 정렬)
  const optionalItems = useMemo(() => {
    if (!componentsToFormsData) return [];
    return componentsToFormsData
      .filter((item) => getOptionalLabel(item))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [componentsToFormsData]);

  // 렌더할 항목 필터 (optional 아니거나 선택된 항목만)
  const itemsToRender = useMemo(() => {
    if (!componentsToFormsData) return [];
    return componentsToFormsData.filter((item) => {
      const opt = getOptionalLabel(item);
      return !opt || visibleOptionalIds.has(item.id);
    });
  }, [componentsToFormsData, visibleOptionalIds]);

  // AllGeneralGradeForm이 optional이고 선택되었는지 확인
  const isGradeFormVisible = useMemo(() => {
    return optionalItems.some(
      (item) =>
        item.components?.component_filename === "AllGeneralGradeForm" &&
        visibleOptionalIds.has(item.id),
    );
  }, [optionalItems, visibleOptionalIds]);

  // AllGeneralGradeForm의 contents 가져오기 (체크박스 표시 조건용)
  const gradeFormContents = useMemo(() => {
    if (!componentsToFormsData) return null;
    const gradeFormItem = componentsToFormsData.find(
      (item) => item.components?.component_filename === "AllGeneralGradeForm",
    );
    if (!gradeFormItem) return null;
    return (
      gradeFormItem.customized_contents ?? gradeFormItem.components?.contents
    );
  }, [componentsToFormsData]);

  // gradePrivacyState 객체
  const gradePrivacyState = useMemo(
    () => ({
      // gradeShowAll,
      gradeColumnVisibility,
    }),
    [gradeColumnVisibility],
  );

  // 마지막 학년도 계산
  const lastSchoolYear = useMemo(() => {
    if (!keyArray || keyArray.length === 0) return null;
    return keyArray[keyArray.length - 1]?.school_year;
  }, [keyArray]);

  // lastYearPrivacyState 객체
  const lastYearPrivacyState = useMemo(
    () => ({
      lastYearShowAll,
      lastYearShow2ndSemester,
      lastSchoolYear,
    }),
    [lastYearShowAll, lastYearShow2ndSemester, lastSchoolYear],
  );

  // optional 항목 토글
  const handleOptionalToggle = (id) => {
    setVisibleOptionalIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // 전체 선택
  const handleSelectAll = () => {
    const allIds = new Set(optionalItems.map((item) => item.id));
    setVisibleOptionalIds(allIds);
  };

  // 교과학습 컬럼 토글
  const handleGradeColumnToggle = (column) => {
    setGradeColumnVisibility((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  // 교과학습 항목 표시 전체 토글
  // const handleGradeShowAllToggle = (checked) => {
  //   setGradeShowAll(checked);
  //   if (checked) {
  //     // 전체 체크
  //     setGradeColumnVisibility({
  //       rank: true,
  //       convert100Ratio: true,
  //       convert100: true,
  //       studentCount: true,
  //       standardDeviation: true,
  //       subjectAverage: true,
  //     });
  //   }
  // };

  // 마지막 학년도 전체 토글
  const handleLastYearShowAllToggle = (checked) => {
    setLastYearShowAll(checked);
    if (!checked) {
      // 전체 uncheck → 2학기도 자동 uncheck
      setLastYearShow2ndSemester(false);
    }
  };

  // 마지막 학년도 2학기 토글
  const handleLastYearShow2ndSemesterToggle = (checked) => {
    setLastYearShow2ndSemester(checked);
  };

  useEffect(() => {
    //초기에 학생과 과정 username, fullname, 학년도가 있는 row가 되도로 개향 한다.
    if (
      settingData &&
      settingData.length > 0 &&
      registrationInfoData &&
      activeMenu?.description
    ) {
      //학생 리스트
      let finalArray;
      finalArray = generateAllRegisteredStudents(
        registrationInfoData,
        settingData,
      );

      if (activeMenu.description !== "통합") {
        finalArray = finalArray.filter((row) =>
          row.extra_school_stage
            ? row.extra_school_stage === activeMenu.description
            : row[1] === activeMenu.description,
        );
      }

      // if (activeMenu.description !== "통합") {
      //   finalArray = finalArray.filter((row) => {
      //     console.log("Current row:", row);
      //     console.log("activeMenu.description:", activeMenu.description);

      //     const isMatch = row.extra_school_stage
      //       ? row.extra_school_stage === activeMenu.description
      //       : row[1] === activeMenu.description;

      //     console.log("row.extra_school_stage:", row.extra_school_stage);
      //     console.log("row[1]:", row[1]);
      //     console.log("isMatch:", isMatch);

      //     if (!isMatch) {
      //       console.log("Filtered out row:", row);
      //     }

      //     return isMatch;
      //   });
      // }

      setRegistrationArray(finalArray);
      // setStudentList(finalArray);

      if (hasPermission(null, "can_read")) {
        setStudentList(finalArray);
      } else if (checkHrteachers(hrTeachersData)) {
        const filteredRows = finalArray.filter((r) =>
          hrTeachersData.find((data) => data.username === r.username),
        );
        setStudentList(filteredRows);

        handleToastCenterBottom(t("common.messages.homeroomReadOnly"));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeMenu.description,
    registrationInfoData,
    settingData,
    hrTeachersData,
    hasPermission,
  ]);

  const handleQueryCondition = (item) => {
    if (item?.id) {
      setFormQueryConditions((prev) => ({
        ...prev,
        form_id: item.id,
      }));
    }
    setFilters((filters) => ({
      ...filters,
      selectedStudent: "",
      schoolYearId: "",
    }));
    setSearchKey("");
    setUsernameList([]);
    setKeyArray([]);
  };

  const handleSearch = (e) => {
    const { value } = e.target;
    setSearchKey(value);
    if (value === "") {
      setUsernameList([]);
    } else if (studentList) {
      const filteredRows = studentList.filter((row) =>
        row.fullname.toLowerCase().includes(value.toLowerCase()),
      );

      // 고유한 username을 기준으로 중복 제거
      const uniqueFilteredRows = Array.from(
        new Map(filteredRows.map((item) => [item.username, item])).values(),
      );

      // 고유한 항목으로 setUsernameList 업데이트
      setUsernameList(uniqueFilteredRows);
    }
  };

  const handleSelectUsername = (e) => {
    const { value } = e.target;
    // console.log("value", value);
    setFilters((filters) => ({
      ...filters,
      selectedStudent: value,
    }));

    const makeArray = registrationArray.filter(
      (row) => row.username.toString() === value,
    );
    setKeyArray(makeArray);
  };

  const handleDropdownChange = (e) => {
    const { name, value } = e.target;
    const selectedOptionText = e.target.selectedOptions[0].text;
    if (name === "0") {
      setFilters((filters) => ({
        ...filters,
        schoolYear: selectedOptionText,
        schoolYearId: value,
      }));
      setUsernameList([]);

      const makeArray = registrationArray.filter(
        (row) => row.school_year.toString() === selectedOptionText,
      );

      setStudentList(makeArray);
    }
  };

  const handleModifyMode = (e) => {
    e.preventDefault();

    if (filters.selectedStudent) {
      if (
        !hasPermission(
          "",
          "can_update",
          checkHrteachers(hrTeachersData, filters.selectedStudent),
        )
      ) {
        handleToastCenterTop(t("common.messages.homeroomModifyOnly"));
        return;
      }

      const onOff = e.target.name;
      if (onOff === "modifyOn") {
        setModifyMode(false);
      } else {
        setModifyMode(true);
      }
    } else {
      console.log("학생아이디가 선택 안됨.");
      handleToastCenterTop(
        t("teacher.printingRelated.printAfterSelectUsername"),
      );
    }
  };

  const adminMode = () => {
    console.log("adminMode 임");
  };

  const schoolLifeDataValue = useMemo(
    () => flexibleData?.[0]?.data_value || {},
    [flexibleData],
  );
  const footerConfig = schoolLifeDataValue?.footer;

  const normalizedFooterConfig = useMemo(() => {
    if (!footerConfig || typeof footerConfig !== "object") return null;

    return {
      enabled: footerConfig.enabled === true,
      left: normalizeFooterSlot(
        footerConfig.left,
        DEFAULT_PRINT_FOOTER_SLOTS.left,
      ),
      center: normalizeFooterSlot(
        footerConfig.center,
        DEFAULT_PRINT_FOOTER_SLOTS.center,
      ),
      right: normalizeFooterSlot(
        footerConfig.right,
        DEFAULT_PRINT_FOOTER_SLOTS.right,
      ),
    };
  }, [footerConfig]);

  const footerCss = useMemo(() => {
    if (!showPageNumbers) return "";

    const legacyUseOtherName = schoolLifeDataValue?.otherName;
    const isLegacyBottomEnabled = schoolLifeDataValue?.customBottom;
    const issueDate = dateTokoreanStyleYearMonthDay(new Date());
    const schoolName = resolveSchoolDisplayName(settingData, schoolId);
    const fullName = keyArray[0]?.fullname || "";
    const otherName = keyArray[0]?.other_name || "";

    if (normalizedFooterConfig?.enabled) {
      const context = {
        issueDate,
        schoolName,
        fullName,
        otherName,
        useOtherName: legacyUseOtherName,
      };

      const leftContent = buildFooterSlotContent(
        normalizedFooterConfig.left,
        context,
      );
      const centerContent = buildFooterSlotContent(
        normalizedFooterConfig.center,
        context,
      );
      const rightContent = buildFooterSlotContent(
        normalizedFooterConfig.right,
        context,
      );

      return `
        @bottom-left {
          content: ${leftContent || '""'};
          font-size: 10pt;
          margin-bottom: 5mm;
        }
        @bottom-center {
          content: ${centerContent || '""'};
          font-size: 10pt;
          margin-bottom: 5mm;
        }
        @bottom-right {
          content: ${rightContent || '""'};
          font-size: 10pt;
          margin-bottom: 5mm;
        }
      `;
    }

    if (isLegacyBottomEnabled && keyArray?.length > 0) {
      return `
        @bottom-left {
          content: "${escapeForCssContent(keyArray[0]["0"] || "")}";
          font-size: 10pt;
          margin-bottom: 5mm;
        }
        @bottom-center {
          content: "${escapeForCssContent(issueDate)}" " " counter(page) " / " counter(pages);
          font-size: 10pt;
          margin-bottom: 5mm;
        }
        @bottom-right {
          content: "${escapeForCssContent(
            `성명: ${legacyUseOtherName ? otherName || fullName : fullName}`,
          )}";
          font-size: 10pt;
          margin-bottom: 5mm;
        }
      `;
    }

    return `
      @bottom-center {
        content: counter(page);
        font-size: 10pt;
        margin-bottom: 5mm;
      }
    `;
  }, [
    showPageNumbers,
    schoolLifeDataValue,
    settingData,
    schoolId,
    keyArray,
    normalizedFooterConfig,
  ]);

  const triggerPrint = useReactToPrint({
    content: () => {
      // addPageNumbers();
      return contentToPrint.current;
    },
    documentTitle: `${keyArray[0]?.["1"] || ""}-생활기록부-${
      keyArray[0]?.fullname
    }`,
    onBeforePrint: () => {
      const content = contentToPrint.current;
      if (content) {
        content.style.width = "210mm";
        content.style.height = "auto";
      }
      console.log("before printing...");
    },
    removeAfterPrint: true,
    pageStyle: `          
     @media print {
      @page {
        size: A4;
        margin:         ${
          showPageNumbers ? `10mm 10mm 15mm 10mm` : `10mm 10mm 10mm 10mm`
        };
        ${footerCss}
        }      }
      
      body {
        counter-reset: page;
        background-color: transparent !important;       
      }
      
      .print-page {
        counter-increment: page;
      }

      thead {
        display: table-row-group;
      }
      
      tfoot {
        display: table-row-group;
      }
      

    }
    `,
  });

  /* 이거 뺐음
      table {
        page-break-inside: auto;
      }
      
      tr {
        page-break-inside: avoid;
        page-break-after: auto;
      }
      
      thead {
        display: table-header-group;
      }
      
      tfoot {
        display: table-footer-group;
      }
*/

  const contentToPrint = useRef(null);
  const handlePrint = () => {
    if (filters.selectedStudent) {
      triggerPrint();
    } else {
      console.log(
        "keyArray is incomplete. Please ensure all data is available.",
      );
      handleToastCenterTop(t("common.messages.printAfterSelectStudent"));
    }
  };

  if (isLoading) {
    return <LoadingInside />;
  }

  if (!hasPermission(null, "can_access", checkHrteachers(hrTeachersData))) {
    return (
      <SimpleAlertMessageBlock
        message={
          t("common.messages.homeroomAccessOnly") ||
          "담임 또는 접근 권한이 있는 사람만 접근할 수 있습니다."
        }
      />
    );
  }

  return (
    <div className="basicWidth">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12 col-md-2">
            <LeftMenuSidebar
              activeMenu={activeMenu}
              setActiveMenu={setActiveMenu}
              menuData={allFormsData}
              extraFunction={handleQueryCondition}
              itemName="form_name"
              itemLabel="form_label"
            />
          </div>
          <div className="col-12 col-md-10">
            <div className="d-flex justify-content-end flex-wrap mt-2">
              <div style={{ width: "150px" }} className="me-1">
                <DropdownSchoolYear
                  settingData={settingData}
                  handleDropdownChange={handleDropdownChange}
                  schoolYearId={filters.schoolYearId}
                  allYears={false}
                  title={t("teacher.printingRelated.allSchoolYears")}
                  disabled={modifyMode}
                />
              </div>

              <div style={{ width: "100px" }} className="me-3 text-end">
                <label
                  htmlFor="searchKeyword"
                  className="col-form-label text-end"
                >
                  {t("common.labels.search")}
                </label>
              </div>
              <div style={{ width: "150px" }} className="me-1">
                <input
                  type="text"
                  id="searchKeyword"
                  name="searchKeyword"
                  className="form-control"
                  placeholder={t("common.user.studentName") || "학생이름"}
                  value={searchKey}
                  onChange={handleSearch}
                  disabled={modifyMode}
                />
              </div>
              <div style={{ width: "150px" }} className="me-1">
                <select
                  className="form-control"
                  name="username"
                  onChange={handleSelectUsername}
                  value={filters.selectedStudent || ""}
                  disabled={modifyMode}
                >
                  <option value="">
                    {t("teacher.printingRelated.selectUsername")}
                  </option>
                  {usernameList &&
                    usernameList.map((user) => (
                      <option key={user.id} value={user.username}>
                        {user.username}
                      </option>
                    ))}
                </select>
              </div>
            </div>
            <div className="d-flex justify-content-end flex-wrap mb-3 mt-2">
              <SchoolLifeRecordFormButtonBar
                handlePrint={handlePrint}
                handleModifyMode={handleModifyMode}
                adminMode={adminMode}
                contentToPrint={contentToPrint}
                modifyMode={modifyMode}
                showPageNumbers={showPageNumbers}
                setShowPageNumbers={setShowPageNumbers}
                showAdditionalInfo={showAdditionalInfo}
                setShowAdditionalInfo={setShowAdditionalInfo}
                componentsToFormsData={componentsToFormsData}
              />
            </div>

            {/* 학교생활기록 항목 선택 UI (NICE 스타일) */}
            {optionalItems.length > 0 && (
              <div className={styles.optionalSection}>
                <div className={styles.optionalHeader}>
                  <span className={styles.optionalTitle}>학교생활기록</span>
                  <button
                    type="button"
                    className={styles.selectAllBtn}
                    onClick={handleSelectAll}
                  >
                    전체 선택
                  </button>
                </div>
                <div className={styles.optionalFlex}>
                  {optionalItems.map((item, index) => (
                    <label key={item.id} className={styles.optionalCheckbox}>
                      <input
                        type="checkbox"
                        checked={visibleOptionalIds.has(item.id)}
                        onChange={() => handleOptionalToggle(item.id)}
                      />
                      <span>
                        {/* {index + 1}. {getOptionalLabel(item)} */}
                        {getOptionalLabel(item)}
                      </span>
                    </label>
                  ))}
                </div>

                {/* 교과학습 항목 표시 (AllGeneralGradeForm이 선택된 경우만) */}
                {isGradeFormVisible && gradeFormContents && (
                  <div className={styles.gradePrivacySection}>
                    {/* <label className={styles.gradePrivacyMain}>
                      <input
                        type="checkbox"
                        checked={gradeShowAll}
                        onChange={(e) =>
                          handleGradeShowAllToggle(e.target.checked)
                        }
                      />
                      <span>교과학습 항목 표시</span>
                    </label> */}
                    <div className={styles.gradeColumnOptions}>
                      <label>
                        <span className={styles.optionalTitle}>
                          교과학습 항목 표시
                        </span>
                      </label>
                      {!gradeFormContents?.subjectAverageAnti && (
                        <label>
                          <input
                            type="checkbox"
                            checked={gradeColumnVisibility.subjectAverage}
                            onChange={() =>
                              handleGradeColumnToggle("subjectAverage")
                            }
                          />
                          <span>과목평균</span>
                        </label>
                      )}

                      {gradeFormContents?.convert100 && (
                        <label>
                          <input
                            type="checkbox"
                            checked={gradeColumnVisibility.convert100}
                            onChange={() =>
                              handleGradeColumnToggle("convert100")
                            }
                          />
                          <span>성취도</span>
                        </label>
                      )}
                      {gradeFormContents?.convert100Ratio && (
                        <label>
                          <input
                            type="checkbox"
                            checked={gradeColumnVisibility.convert100Ratio}
                            onChange={() =>
                              handleGradeColumnToggle("convert100Ratio")
                            }
                          />
                          <span>성취도별 분포비율</span>
                        </label>
                      )}

                      {gradeFormContents?.standardDeviation && (
                        <label>
                          <input
                            type="checkbox"
                            checked={gradeColumnVisibility.standardDeviation}
                            onChange={() =>
                              handleGradeColumnToggle("standardDeviation")
                            }
                          />
                          <span>표준편차</span>
                        </label>
                      )}
                      {gradeFormContents?.rank && (
                        <label>
                          <input
                            type="checkbox"
                            checked={gradeColumnVisibility.rank}
                            onChange={() => handleGradeColumnToggle("rank")}
                          />
                          <span>석차</span>
                        </label>
                      )}

                      {gradeFormContents?.rankClass && (
                        <label>
                          <input
                            type="checkbox"
                            checked={gradeColumnVisibility.rankClass}
                            onChange={() =>
                              handleGradeColumnToggle("rankClass")
                            }
                          />
                          <span>석차등급</span>
                        </label>
                      )}
                      {gradeFormContents?.studentCount && (
                        <label>
                          <input
                            type="checkbox"
                            checked={gradeColumnVisibility.studentCount}
                            onChange={() =>
                              handleGradeColumnToggle("studentCount")
                            }
                          />
                          <span>수강자수</span>
                        </label>
                      )}
                    </div>
                  </div>
                )}

                {/* 마지막 학년도 일부정보 미제공 */}
                {lastSchoolYear && (
                  <div className={styles.gradePrivacySection}>
                    {/* <label className={styles.gradePrivacyMain}>
                      <span>마지막 학년도 일부정보 미제공</span>
                    </label> */}
                    <div className={styles.gradeColumnOptions}>
                      <label>
                        <span className={styles.optionalTitle}>
                          마지막 학년도 일부정보 미제공
                        </span>
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={lastYearShowAll}
                          onChange={(e) =>
                            handleLastYearShowAllToggle(e.target.checked)
                          }
                        />
                        <span>{lastSchoolYear}년 전체</span>
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={lastYearShow2ndSemester}
                          onChange={(e) =>
                            handleLastYearShow2ndSemesterToggle(
                              e.target.checked,
                            )
                          }
                          disabled={!lastYearShowAll}
                        />
                        <span>2학기</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div
              id="gradeReportPrintArea"
              className="d-flex justify-content-center print-page"
              ref={contentToPrint}
            >
              <div className={styles.printContent} style={{ width: "210mm" }}>
                <div className="p-2">
                  {itemsToRender &&
                    itemsToRender.map((item, index) => (
                      <DynamicComponentsRenderer
                        components={item.components}
                        customizedContents={item.customized_contents}
                        modifyMode={modifyMode}
                        setIsLoadingModal={setIsLoadingModal}
                        key={`${item.id}-${item.order}-${index}`}
                        componentsToFormId={item.id}
                        keyArray={keyArray}
                        activeMenu={activeMenu}
                        showAdditionalInfo={showAdditionalInfo}
                        gradePrivacyState={gradePrivacyState}
                        lastYearPrivacyState={lastYearPrivacyState}
                      />
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
